import { ThemeOptions } from "@mui/material";
import type { SafeAreaInsets } from "capacitor-plugin-safe-area";
import { isIosNativeNavigation } from "./nativeNavigation/platform";

/**
 * UIKit's fixed height for a regular navigation bar, hardcoded because the navigation plugin
 * reports no metrics. Screens that hide their bar get 44pt they do not need.
 */
const NATIVE_NAV_BAR_HEIGHT = 44;

// some theme config that depends on state
//
// This used to read `windowSizes.value` itself. It is called from App.tsx's render, so
// under signals that read silently subscribed App to windowSizes; converting it to
// `getState()` would have frozen the drawer/backdrop/snackbar insets at their startup
// values. App.tsx already selects the insets explicitly for the useMemo dep array, so
// they are passed in instead and the subscription stays visible at the one call site.
// The return type used to be asserted `as Theme`, which was a lie — this is a partial
// options object, not a built theme — and the assertion suppressed any type error MUI 9
// would otherwise have raised on the shape below. Declaring the honest `ThemeOptions`
// return type type-checks the overrides for real; they pass unchanged under v9.
export const themeOverride = (insets: SafeAreaInsets["insets"] | undefined): ThemeOptions => {
  const bottom = insets?.bottom ?? 0;
  const top = insets?.top ?? 0;

  return {
    components: {
      MuiDrawer: {
        styleOverrides: {
          paper: {
            paddingBottom: bottom + "px",
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            paddingBottom: bottom + "px",
          },
        },
      },
      MuiSnackbar: {
        styleOverrides: {
          root: {
            // A fixed-position snackbar sits against the webview viewport, which under
            // native navigation runs beneath the translucent bar. The device inset clears
            // only the status bar, so the bar's own height has to be added on top.
            paddingTop: top + (isIosNativeNavigation() ? NATIVE_NAV_BAR_HEIGHT : 0) + "px",
          },
        },
      },
    },
  };
};
